/**
 * Utilitários compartilhados do tema (sem dependências).
 */
(() => {
  const loadedScripts = new Map();

  /** Carrega um script externo uma única vez; devolve uma Promise resolvida quando estiver disponível. */
  function loadScript(src) {
    if (loadedScripts.has(src)) return loadedScripts.get(src);
    const promise = new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing && existing.dataset.loaded) return resolve();
      const script = existing || document.createElement('script');
      script.addEventListener('load', () => {
        script.dataset.loaded = 'true';
        resolve();
      });
      script.addEventListener('error', reject);
      if (!existing) {
        script.src = src;
        script.defer = true;
        document.head.appendChild(script);
      }
    });
    loadedScripts.set(src, promise);
    return promise;
  }

  /** Carrega o Swiper (assets/swiper-bundle.min.js) a partir da URL informada pela seção. */
  function loadSwiper(src) {
    if (window.Swiper) return Promise.resolve(window.Swiper);
    return loadScript(src).then(() => window.Swiper);
  }

  /** Executa uma função na primeira interação do usuário (scroll, mousemove, touch, tecla) ou após o timeout. */
  function onFirstInteraction(callback, timeout = 6000) {
    let done = false;
    const events = ['scroll', 'mousemove', 'touchstart', 'keydown'];
    const run = () => {
      if (done) return;
      done = true;
      events.forEach((event) => window.removeEventListener(event, run));
      callback();
    };
    events.forEach((event) => window.addEventListener(event, run, { once: true, passive: true }));
    setTimeout(run, timeout);
  }

  /** localStorage com expiração (em dias), tolerante a navegadores sem storage. */
  const storage = {
    set(key, value, days) {
      try {
        const expires = Date.now() + days * 24 * 60 * 60 * 1000;
        localStorage.setItem(key, JSON.stringify({ value, expires }));
      } catch (error) {
        /* storage indisponível */
      }
    },
    get(key) {
      try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const data = JSON.parse(raw);
        if (data.expires && Date.now() > data.expires) {
          localStorage.removeItem(key);
          return null;
        }
        return data.value;
      } catch (error) {
        return null;
      }
    },
  };

  /** Prende o foco dentro de um elemento (drawers e popups). Devolve a função que libera o foco. */
  function trapFocus(container, initialFocus) {
    const selector = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';
    const focusable = () => Array.from(container.querySelectorAll(selector)).filter((el) => el.offsetParent !== null);
    const handler = (event) => {
      if (event.key !== 'Tab') return;
      const items = focusable();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    container.addEventListener('keydown', handler);
    const target = initialFocus || focusable()[0];
    if (target) target.focus();
    return () => container.removeEventListener('keydown', handler);
  }

  /**
   * Section Rendering API: busca o HTML de uma seção na URL informada.
   * Devolve o documento já parseado, para quem precisa de um pedaço (grade, contador) em vez da seção inteira.
   */
  async function fetchSection(sectionId, url) {
    const target = new URL(url, window.location.origin);
    target.searchParams.set('section_id', sectionId);
    const response = await fetch(target.toString());
    if (!response.ok) throw new Error(`Section Rendering: ${response.status}`);
    const text = await response.text();
    return new DOMParser().parseFromString(text, 'text/html');
  }

  /** Troca o conteúdo de uma seção e avisa os módulos que observam seções (images.js). */
  function replaceSection(sectionId, doc) {
    const current = document.getElementById(`shopify-section-${sectionId}`);
    const next = doc.getElementById(`shopify-section-${sectionId}`) || doc.body;
    if (!current) return null;
    current.innerHTML = next.innerHTML;
    current.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
    return current;
  }

  /** Dispara um evento global do tema. */
  function emit(name, detail = {}) {
    document.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
  }

  /**
   * Formata centavos usando o formato de moeda da loja (window.theme.moneyFormat). Fora do Brasil
   * (window.theme.intl.active), mostra em dólar pelo câmbio do site — o mesmo cálculo do snippet `money`
   * (centavos ÷ câmbio, arredondado ao centavo). A cobrança continua em reais.
   */
  function formatMoney(cents) {
    const intl = window.theme && window.theme.intl;
    if (intl && intl.active && Number(intl.rate) > 0) {
      const usd = Math.round(Number(cents) / Number(intl.rate));
      const whole = Math.floor(usd / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return `$${whole}.${String(usd % 100).padStart(2, '0')}`;
    }
    const format = (window.theme && window.theme.moneyFormat) || 'R$ {{amount_with_comma_separator}}';
    const value = (Number(cents) / 100).toFixed(2);
    const [int, dec] = value.split('.');
    const dotted = int.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const commas = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format
      .replace('{{amount_with_comma_separator}}', `${dotted},${dec}`)
      .replace('{{amount_no_decimals_with_comma_separator}}', dotted)
      .replace('{{amount_no_decimals}}', commas)
      .replace('{{amount}}', `${commas}.${dec}`);
  }

  /**
   * Atualiza um bloco de preço renderizado pelo snippet `price` (preço, preço original e parcelas)
   * para a variante informada. Usado pelo card da vitrine e pela PDP.
   */
  function updatePriceBlock(block, variant) {
    if (!block || !variant) return;
    const price = block.querySelector('[data-price]');
    const compare = block.querySelector('[data-compare-price]');
    const soldOut = block.querySelector('.cmp-price-sold-out');
    const installments = block.querySelector('[data-installments]');
    if (price) price.textContent = formatMoney(variant.price);
    if (soldOut) soldOut.hidden = Boolean(variant.available);
    if (compare) {
      const hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
      compare.textContent = hasCompare ? formatMoney(variant.compare_at_price) : '';
      compare.parentElement.hidden = !hasCompare;
    }
    if (installments) {
      const max = Number((window.theme && window.theme.installmentsMax) || 6);
      const min = Number((window.theme && window.theme.installmentMinValue) || 60) * 100;
      let count = min > 0 ? Math.floor(variant.price / min) : 1;
      count = Math.max(1, Math.min(max, count));
      const template = (window.theme && window.theme.strings && window.theme.strings.installments) || '';
      // O bloco a esconder é o wrapper .cmp-price-installments; o texto fica no <p> interno.
      const box = installments.closest('.cmp-price-installments') || installments;
      const intl = window.theme && window.theme.intl && window.theme.intl.active;
      box.hidden = count < 2 || !variant.available || !template || Boolean(intl);
      if (count >= 2 && template) {
        installments.textContent = template.replace('[count]', count).replace('[amount]', formatMoney(Math.floor(variant.price / count)));
      }
    }
  }

  /**
   * Atualiza a flag de desconto (o ::before com --discount do snippet `flag`) para a variante informada.
   */
  function updateDiscountFlag(container, variant) {
    if (!container) return;
    const hasCompare = variant && variant.compare_at_price && variant.compare_at_price > variant.price;
    const percent = hasCompare ? Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price) : 0;
    const template = (window.theme && window.theme.strings && window.theme.strings.discountFlag) || '[percent]%OFF';
    if (percent > 0) container.style.setProperty('--discount', `'${template.replace('[percent]', percent)}'`);
    else container.style.removeProperty('--discount');
    container.classList.toggle('has-content', percent > 0 || container.querySelector('.flag') !== null);
  }

  /**
   * Preço em dólar no checkout internacional (aprovado pelo cliente em 23/10/2026). Sem Shopify Payments o
   * checkout só mostra e cobra em reais; o que dá é a propriedade "Price in USD" em cada peça, que a Shopify
   * mostra sozinha embaixo da peça no checkout, no e-mail e no pedido. O valor é o preço unitário da peça pelo
   * câmbio do site (formatMoney), o mesmo da página do produto. No Brasil a peça vai sem a propriedade.
   */
  const USD_PROPERTY = 'Price in USD';

  /** Propriedades com o "Price in USD" fora do Brasil, ou sem ele no Brasil (e quando o preço é zero). */
  function usdProperties(cents, properties) {
    const result = { ...(properties || {}) };
    delete result[USD_PROPERTY];
    const intl = window.theme && window.theme.intl;
    if (intl && intl.active && Number(intl.rate) > 0 && Number(cents) > 0) result[USD_PROPERTY] = formatMoney(cents);
    return result;
  }

  /** Linha do /cart/add.js em JSON com o "Price in USD"; no Brasil volta igual (sem `properties` vazio). */
  function withUsdPrice(line, cents) {
    const properties = usdProperties(cents, line.properties);
    return Object.keys(properties).length ? { ...line, properties } : line;
  }

  /** O mesmo para os formulários de compra enviados como FormData (página do produto e card). */
  function appendUsdPrice(formData, cents) {
    const value = usdProperties(cents)[USD_PROPERTY];
    if (value) formData.set(`properties[${USD_PROPERTY}]`, value);
    return formData;
  }

  /**
   * Antes do checkout, acerta o "Price in USD" de cada linha: põe nas peças que entraram sem ele (adicionadas
   * no Brasil antes de trocar para o internacional), tira no Brasil e atualiza quando o câmbio ou o preço
   * mudou. O brinde (`_brinde`) e a linha de graça ficam sem. Uma linha por vez, relendo o carrinho, porque
   * trocar as propriedades muda a chave da linha.
   */
  async function syncUsdPrices() {
    const { cart: cartUrl, cartAdd, cartChange } = window.theme.routes;
    const post = (url, body) => fetch(`${url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    for (let guard = 0; guard < 50; guard += 1) {
      const cart = await (await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } })).json();
      const wanted = cart.items.map((item) => {
        const free = (item.properties && '_brinde' in item.properties) || item.final_line_price === 0;
        return usdProperties(free ? 0 : item.original_price, item.properties);
      });
      const index = cart.items.findIndex((item, i) => ((item.properties || {})[USD_PROPERTY] || '') !== (wanted[i][USD_PROPERTY] || ''));
      if (index < 0) return;
      const item = cart.items[index];
      const properties = wanted[index];
      if (properties[USD_PROPERTY]) {
        if (!(await post(cartChange, { line: index + 1, quantity: item.quantity, properties })).ok) return;
      } else {
        // O /cart/change.js não apaga propriedade (`{}` é ignorado e '' fica gravado vazio): tira a linha e põe
        // de novo sem o "Price in USD". Tirar antes libera o estoque da peça de 1 unidade.
        if (!(await post(cartChange, { id: item.key, quantity: 0 })).ok) return;
        const line = { id: item.variant_id, quantity: item.quantity };
        if (Object.keys(properties).length) line.properties = properties;
        if (!(await post(cartAdd, { items: [line] })).ok) return;
      }
    }
  }

  // Botão de finalizar do carrinho (snippet cart-summary, no drawer e na página /cart): acerta as linhas e
  // reenvia o mesmo formulário. Se demorar mais de 5 s ou falhar, segue para o checkout assim mesmo.
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.classList.contains('checkout-form') || form.dataset.usdReady) return;
    event.preventDefault();
    if (form.dataset.usdSyncing) return;
    form.dataset.usdSyncing = 'true';
    const button = event.submitter || form.querySelector('[name="checkout"]');
    await Promise.race([syncUsdPrices().catch(() => {}), new Promise((resolve) => setTimeout(resolve, 5000))]);
    delete form.dataset.usdSyncing;
    form.dataset.usdReady = 'true';
    form.requestSubmit(button);
    delete form.dataset.usdReady;
  });

  window.MB = Object.assign(window.MB || {}, { loadScript, loadSwiper, onFirstInteraction, storage, trapFocus, fetchSection, replaceSection, emit, formatMoney, updatePriceBlock, updateDiscountFlag, withUsdPrice, appendUsdPrice, syncUsdPrices });
})();
